import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonToolbar,
    IonPage,
    IonFooter,
    IonRow,
    IonCol,
    IonTitle,
    IonModal,
    IonTextarea,
    IonCheckbox, IonToggle
} from "@ionic/react";
import { DetailType, REST_AEROTOW_URL, REST_PILOT_URL, TimeType } from "../common/constants";
import { TimeInput } from "./TimeInput";
import { arrowBack, save, trash } from "ionicons/icons";
import { createTow, deleteResource, updateResource } from "../common/ApiUtils";
import PilotROList from "../pilot/PilotROList";
import { TowingPlaneList } from "../towingPlane/TowingPLaneList";
import { PlaceList } from "../place/PlaceList";
import { findGliderFce, findPilotFce } from "../common/FindFuctions";
import "./AeroTowDetail.css";
import GliderListRO from "../glider/GliderListRO";
import { completePilotName } from "../common/StringUtils";
import TowTaskCompose from "../towtask/TowTaskCompose";
import {convTime} from "../common/TimeUtils";

// Custom hooks for better separation of concerns
const useViewNavigation = (initialView = DetailType.HOME) => {
    const [view, setView] = useState(initialView);

    const navigateTo = useCallback((viewType) => {
        setView(viewType);
    }, []);

    const navigateHome = useCallback(() => {
        setView(DetailType.HOME);
    }, []);

    return { view, navigateTo, navigateHome };
};

const useDeleteModal = () => {
    const [isOpen, setIsOpen] = useState(false);

    const openModal = useCallback(() => setIsOpen(true), []);
    const closeModal = useCallback(() => setIsOpen(false), []);

    return { isOpen, openModal, closeModal };
};

// Navigation component for better reusability
const BackButton = ({ onBack, label = "Zpět" }) => (
    <IonButton slot="start" color="primary" onClick={onBack}>
        <IonIcon icon={arrowBack} /> {label}
    </IonButton>
);

// Main component
const AeroTowDetail = ({ tow, active = {}, home, timeChange, changeTow, date, onError, onTowPersisted, getFlyDay }) => {
    const { view, navigateTo, navigateHome } = useViewNavigation();
    const deleteModal = useDeleteModal();
    const [towToUpdate, setTowToUpdate]  = useState(tow);
    const [reel, setReel]  = useState(false);
    // Memoized values
    const isSuggestion = useMemo(() => tow?.suggest, [tow?.suggest]);

    const towRef = useRef(tow);
    towRef.current = tow;

    const activeRef = useRef(active);
    activeRef.current = active;

    const activeMergeKey = useMemo(
        () =>
            [
                active?.lifter?.id,
                active?.first?.id,
                active?.second?.id,
                active?.glider?.id,
                active?.towingPlane?.id,
                active?.reel ? "1" : "0",
            ].join("|"),
        [active]
    );

    const towServerRev = useMemo(() => {
        if (!tow) return "";
        const g = tow.glider;
        const tp = tow.towingPlane;
        return [
            tow.id ?? "new",
            tow.uuid ?? "",
            tow.updatedDate ?? "",
            tow.note ?? "",
            tow.forcedGliderLanding ?? "",
            tow.forcedTowingPlaneLanding ?? "",
            tow.gliderLanding ?? "",
            tow.towingPlaneLanding ?? "",
            tow.start ?? "",
            tow.first?.id ?? "",
            tow.second?.id ?? "",
            tow.lifter?.id ?? "",
            g?.id ?? "",
            tp?.id ?? "",
            tow.gliderTask?.id ?? "",
            tow.towTask?.id ?? "",
            tow.takeOffPlace?.id ?? "",
            tow.landingPlace?.id ?? "",
        ].join("|");
    }, [tow]);

    useEffect(() => {
        if (view !== DetailType.HOME) return;
        const t = towRef.current;
        if (!t) return;
        const act = activeRef.current;
        setTowToUpdate((prev) => {
            let base = { ...t };
            if (t.suggest && prev) {
                const sameSuggestion = prev.uuid === t.uuid;
                base = {
                    ...base,
                    glider: t.glider ?? (sameSuggestion ? prev.glider : null),
                    towingPlane: t.towingPlane ?? (sameSuggestion ? prev.towingPlane : null),
                    towTask: t.towTask ?? (sameSuggestion ? prev.towTask : null),
                    lifter: t.lifter ?? (sameSuggestion ? prev.lifter : null),
                    first: t.first ?? (sameSuggestion ? prev.first : null),
                    second: t.second ?? (sameSuggestion ? prev.second : null),
                    gliderLanding: t.gliderLanding ?? (sameSuggestion ? prev.gliderLanding : null),
                    towingPlaneLanding: t.towingPlaneLanding ?? (sameSuggestion ? prev.towingPlaneLanding : null),
                };
            }
            if (base.suggest && act) {
                const gliderMatch =
                    base.glider &&
                    act.glider &&
                    (base.glider.id === act.glider.id ||
                        (base.glider.registration &&
                            act.glider.registration &&
                            base.glider.registration === act.glider.registration));
                if (!base.first && act.first && gliderMatch) {
                    base = { ...base, first: act.first };
                }
                if (!base.second && act.second && gliderMatch && base.glider?.twoSeat) {
                    base = { ...base, second: act.second };
                }
            }
            return base;
        });
    }, [towServerRev, view, activeMergeKey]);

    // Event handlers
    const handlePilotSelection = useCallback(async (pilot) => {
        if (!towToUpdate) return;

        const updatedTow = { ...towToUpdate };

        switch (view) {
            case DetailType.GLIDER_FIRST:
                updatedTow.first = pilot;
                break;
            case DetailType.GLIDER_SECOND:
                updatedTow.second = pilot;
                break;
            case DetailType.TOWING_PILOT:
                updatedTow.lifter = pilot;
                break;
            default:
                break;
        }
        setTowToUpdate(updatedTow);

        if (towToUpdate.id) {
            const response = await updateResource(REST_AEROTOW_URL, updatedTow);
            if (response && response.error) {
                onError?.(response);
                return;
            }
            onTowPersisted?.(updatedTow);
        }
        navigateHome();
    }, [towToUpdate, view, navigateHome, onError, onTowPersisted]);

    const handleGliderSelection = useCallback(async (glider) => {
        if (!towToUpdate) return;

        const updatedTow = { ...towToUpdate, glider };

        setTowToUpdate(updatedTow);
        if (towToUpdate.id) {
            const response = await updateResource(REST_AEROTOW_URL, updatedTow);
            if (response && response.error) {
                onError?.(response);
                return;
            }
            onTowPersisted?.(updatedTow);
        }
        navigateHome();
    }, [towToUpdate, navigateHome, onError, onTowPersisted]);

    const handleTowingPlaneTaskSelection = useCallback(async (planeTask) => {
        if (!towToUpdate) return;

        const updatedTow = { ...towToUpdate };
        updatedTow.towTask = planeTask;

        setTowToUpdate(updatedTow);
        if (towToUpdate.id) {
            const response = await updateResource(REST_AEROTOW_URL, updatedTow);
            if (response && response.error) {
                onError?.(response);
                return;
            }
            onTowPersisted?.(updatedTow);
        }
        navigateHome();
    }, [towToUpdate, navigateHome, onError, onTowPersisted]);

    const handleTowingPlaneSelection = useCallback(async (plane) => {
        if (!towToUpdate) return;

        const updatedTow = { ...towToUpdate, towingPlane: plane };

        setTowToUpdate(updatedTow);
        if (towToUpdate.id) {
            const response = await updateResource(REST_AEROTOW_URL, updatedTow);
            if (response && response.error) {
                onError?.(response);
                return;
            }
            onTowPersisted?.(updatedTow);
        }
        navigateHome();
    }, [towToUpdate, navigateHome, onError, onTowPersisted]);

    const handlePlaceSelection = useCallback(async (place) => {
        if (!towToUpdate) return;

        const updatedTow = { ...towToUpdate };

        if (view === DetailType.TAKEOFF_PLACE) {
            updatedTow.takeOffPlace = place;
        } else {
            updatedTow.landingPlace = place;
        }

        setTowToUpdate(updatedTow);
        const response = await updateResource(REST_AEROTOW_URL, updatedTow);
        if (response && response.error) {
            onError?.(response);
            return;
        }
        onTowPersisted?.(updatedTow);
        navigateHome();
    }, [towToUpdate, view, navigateHome, onError, onTowPersisted]);

    const handleNoteChange = useCallback(async (value) => {
        if (!towToUpdate?.id) return;

        const ok = await changeTow?.(towToUpdate.id, DetailType.NOTE, value);
        if (ok) {
            setTowToUpdate((prev) => (prev ? { ...prev, note: value } : prev));
            navigateHome();
        }
    }, [towToUpdate?.id, changeTow, navigateHome]);

    const handleForcedGliderLandingChange = useCallback(async (checked) => {
        if (!towToUpdate) return;

        const snapshot = towToUpdate;
        const updatedTow = { ...snapshot, forcedGliderLanding: checked };

        setTowToUpdate(updatedTow);
        const response = await updateResource(REST_AEROTOW_URL, updatedTow);
        if (response && response.error) {
            onError?.(response);
            setTowToUpdate(snapshot);
            return;
        }
        await getFlyDay?.();
    }, [towToUpdate, onError, getFlyDay]);

    const handleForcedTowingPlaneLandingChange = useCallback(async (checked) => {
        if (!towToUpdate) return;

        const snapshot = towToUpdate;
        const updatedTow = { ...snapshot, forcedTowingPlaneLanding: checked };

        setTowToUpdate(updatedTow);
        const response = await updateResource(REST_AEROTOW_URL, updatedTow);
        if (response && response.error) {
            onError?.(response);
            setTowToUpdate(snapshot);
            return;
        }
        await getFlyDay?.();
    }, [towToUpdate, onError, getFlyDay]);

    const handleDelete = useCallback(async () => {
        if (!towToUpdate?.id) return;

        await deleteResource(`${REST_AEROTOW_URL}/${towToUpdate.id}`);
        await getFlyDay?.();
        home?.();
    }, [towToUpdate?.id, getFlyDay, home]);

    const handleTimeChange = useCallback((id, type, value) => {
        if (!type) return;

        const updatedTow = { ...towToUpdate };

        if (type === TimeType.START_TIME) {
            if (!value) return;
            updatedTow.start = convTime(value);
        } else if (type === TimeType.LANDING_GLIDER_TIME) {
            updatedTow.gliderLanding = value ? convTime(value) : null;
        } else if (type === TimeType.LANDING_TOWING_TIME) {
            updatedTow.towingPlaneLanding = value ? convTime(value) : null;
        }
        setTowToUpdate(updatedTow);

        if (!towToUpdate?.suggest && id) {
            timeChange(id, type, value);
        }
    }, [towToUpdate, timeChange]);

    const handleCreateAndHome = useCallback(async () => {
        if (!towToUpdate) return;

        await createTow(towToUpdate, date);
        await getFlyDay?.();
        home?.();
    }, [towToUpdate, date, getFlyDay, home]);

    // Render methods
    const renderRequiredField = (label) => (
        <div className="item-required">vyplnit!</div>
    );

    const renderPilotView = () => {
        const messages = {
            [DetailType.GLIDER_FIRST]: "Vyber prvního pilota kluzáku",
            [DetailType.GLIDER_SECOND]: "Vyber druhého pilota kluzáku",
            [DetailType.TOWING_PILOT]: "Vyber vlekaře"
        };

        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons>
                            <BackButton onBack={navigateHome} />
                            <IonTitle size="small">{messages[view] || "Vyber pilota"}</IonTitle>
                            {view === DetailType.GLIDER_SECOND && (
                                <IonButton
                                    color="primary"
                                    slot="end"
                                    onClick={() => handlePilotSelection(null)}
                                >
                                    Žádný pilot
                                </IonButton>
                            )}
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent>
                    <PilotROList
                        key="pilotList"
                        resourceUrl={REST_PILOT_URL}
                        view={view}
                        active={towToUpdate}
                        setPilot={handlePilotSelection}
                        findFce={findPilotFce}
                        loadApiQuery="active=true"
                    />
                </IonContent>
            </IonPage>
        );
    };

    const renderNoteView = () => {
        let noteRef;

        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <BackButton onBack={navigateHome} />
                        </IonButtons>
                        <IonTitle>Poznámka</IonTitle>
                    </IonToolbar>
                </IonHeader>
                <IonContent>
                    <IonTextarea
                        value={towToUpdate?.note}
                        ref={input => noteRef = input}
                        placeholder="Poznámka"
                    />
                </IonContent>
                <IonFooter>
                    <IonRow>
                        <IonCol sizeXs="12" sizeMd="4" offsetMd="4">
                            <IonButton
                                expand="full"
                                onClick={() => handleNoteChange(noteRef?.value)}
                                color="primary"
                            >
                                Uložit
                            </IonButton>
                        </IonCol>
                    </IonRow>
                </IonFooter>
            </IonPage>
        );
    };

    const renderGliderView = () => (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons>
                        <BackButton onBack={navigateHome} />
                        <IonTitle>Vyber kluzák</IonTitle>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent>
                <GliderListRO
                    key="gliderList"
                    setGlider={handleGliderSelection}
                    active={towToUpdate?.glider}
                    findFce={findGliderFce}
                />
            </IonContent>
        </IonPage>
    );

    const renderTowingTaskView = () => {
        return (
            <TowTaskCompose tow={towToUpdate} setTask={handleTowingPlaneTaskSelection} active={towToUpdate?.towingPlane?.task} back={navigateHome}/>
        );
    };

    const renderTowingPlaneView = () => (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons>
                        <BackButton onBack={navigateHome} />
                        <IonTitle>Vyber vlečné letadlo</IonTitle>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent>
                <TowingPlaneList
                    key="planeList"
                    setPlane={handleTowingPlaneSelection}
                    active={towToUpdate?.towingPlane}
                />
            </IonContent>
        </IonPage>
    );

    const renderPlaceView = () => {
        const isTakeOff = view === DetailType.TAKEOFF_PLACE;
        const place = isTakeOff ? towToUpdate?.takeOffPlace : towToUpdate?.landingPlace;
        const title = isTakeOff ? "Vyber místo vzletu" : "Vyber místo přistání";

        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons>
                            <BackButton onBack={navigateHome} />
                            <IonTitle>{title}</IonTitle>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent>
                    <PlaceList
                        key="placeList"
                        setPlace={handlePlaceSelection}
                        active={place}
                    />
                </IonContent>
            </IonPage>
        );
    };

    const renderDeleteModal = () => (
        <IonModal isOpen={deleteModal.isOpen} backdropDismiss={false}>
            <IonHeader translucent>
                <IonToolbar>
                    <IonTitle>Smazání letu</IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={deleteModal.closeModal}>Zavřít</IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent>
                <IonRow className="ion-align-items-center">
                    <IonCol>
                        <h2>Opravdu smazat?</h2>
                    </IonCol>
                </IonRow>
                <IonRow className="ion-align-items-center">
                    <IonCol>
                        <IonButton onClick={deleteModal.closeModal}>Ne</IonButton>
                        <IonButton onClick={handleDelete}>Ano</IonButton>
                    </IonCol>
                </IonRow>
            </IonContent>
        </IonModal>
    );

    const renderHomeView = () => {
        if (!towToUpdate) return null;

        const {
            id, towingPlane, lifter, glider, first, second, start, towingPlaneLanding,
            gliderLanding, note, landingPlace, takeOffPlace, towTask, forcedGliderLanding,
            forcedTowingPlaneLanding
        } = towToUpdate;

        const towType = (towingPlane || isSuggestion)
            ? (towTask?.onlyTow ? 'Úloha bez kluzáku' : 'Aerovlek')
            : 'Naviják';

        const onlyTowTask = (towingPlane || isSuggestion) && towTask?.onlyTow;

        const isFormValid = ((glider && first && ((towingPlane && lifter)|| reel) && !onlyTowTask) ||
            (onlyTowTask && (towingPlane && lifter))) && start;

        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <BackButton onBack={home} />
                        </IonButtons>
                        <IonButton slot="end" onClick={deleteModal.openModal}>
                            <IonIcon icon={trash} />
                        </IonButton>
                    </IonToolbar>
                </IonHeader>
                <IonContent>

                    <IonList>
                        <IonItem onClick={() => setReel(!reel)}>
                            <IonLabel>Naviják</IonLabel>
                            <IonToggle value="navikák" checked={reel}/>
                        </IonItem>
                        {!reel &&
                            <IonItem onClick={() => navigateTo(DetailType.TOWING_TASK)}>
                                <IonLabel>Typ</IonLabel>
                                <div>{towType}</div>
                            </IonItem>
                        }
                        <IonItem>
                            <IonLabel>Start</IonLabel>
                            <TimeInput
                                value={start}
                                id={id}
                                type={TimeType.START_TIME}
                                timeChange={handleTimeChange}
                                disabled={isSuggestion}
                            />
                        </IonItem>

                        {/* Towing plane section */}
                        { !reel && (towingPlane || isSuggestion) && (
                            <>
                                <IonItem onClick={() => navigateTo(DetailType.TOWING_PLANE)}>
                                    <IonLabel>Vlečná</IonLabel>
                                    {towingPlane ? (
                                        <div>{towingPlane.name} {towingPlane.registration}</div>
                                    ) : (
                                        renderRequiredField()
                                    )}
                                </IonItem>

                                <IonItem onClick={() => navigateTo(DetailType.TOWING_PILOT)}>
                                    <IonLabel>Vlekař</IonLabel>
                                    {lifter ? (
                                        <div>{completePilotName(lifter)}</div>
                                    ) : (
                                        renderRequiredField()
                                    )}
                                </IonItem>

                                <IonItem>
                                    <IonLabel>Přistání vleku</IonLabel>
                                    <TimeInput
                                        value={towingPlaneLanding}
                                        min={start}
                                        id={id}
                                        type={TimeType.LANDING_TOWING_TIME}
                                        timeChange={handleTimeChange}
                                        disabled={isSuggestion}
                                    />
                                </IonItem>

                                {!isSuggestion && (
                                    <IonItem>
                                        <IonCheckbox
                                            justify="space-between"
                                            checked={!!forcedTowingPlaneLanding}
                                            onIonChange={e => handleForcedTowingPlaneLandingChange(e.detail.checked)}
                                        >
                                            Ignorovat automatický čas přistání vleku
                                        </IonCheckbox>
                                    </IonItem>
                                )}
                            </>
                        )}

                        {/* Glider section */}
                        {!onlyTowTask && glider ? (
                            <>
                                <IonItem onClick={() => navigateTo(DetailType.GLIDER)}>
                                    <IonLabel>Kluzák</IonLabel>
                                    <div>{glider.name} {glider.registration}</div>
                                </IonItem>

                                <IonItem onClick={() => navigateTo(DetailType.GLIDER_FIRST)}>
                                    <IonLabel>První pilot</IonLabel>
                                    {first ? (
                                        <div>{completePilotName(first)}</div>
                                    ) : (
                                        renderRequiredField()
                                    )}
                                </IonItem>

                                {glider.twoSeat && (
                                    <IonItem onClick={() => navigateTo(DetailType.GLIDER_SECOND)}>
                                        <IonLabel>Druhý pilot</IonLabel>
                                        <div>
                                            {second ? completePilotName(second) : "žádný"}
                                        </div>
                                    </IonItem>
                                )}

                                <IonItem>
                                    <IonLabel>Přistání kluzáku</IonLabel>
                                    <TimeInput
                                        value={gliderLanding}
                                        id={id}
                                        min={start}
                                        type={TimeType.LANDING_GLIDER_TIME}
                                        timeChange={handleTimeChange}
                                    />
                                </IonItem>

                                {!isSuggestion && (<IonItem>
                                    <IonCheckbox
                                        justify="space-between"
                                        checked={!!forcedGliderLanding}
                                        onIonChange={e => handleForcedGliderLandingChange(e.detail.checked)}
                                    >
                                        Ignorovat automatický čas přistání kluzáku
                                    </IonCheckbox>
                                </IonItem>)
                                }
                            </>
                        ) : (
                            isSuggestion && !onlyTowTask && (
                                <>
                                    <IonItem onClick={() => navigateTo(DetailType.GLIDER)}>
                                        <IonLabel>Kluzák</IonLabel>
                                        {renderRequiredField()}
                                    </IonItem>

                                    <IonItem onClick={() => navigateTo(DetailType.GLIDER_FIRST)}>
                                        <IonLabel>První pilot</IonLabel>
                                        {first ? (
                                            <div>{completePilotName(first)}</div>
                                        ) : (
                                            renderRequiredField()
                                        )}
                                    </IonItem>

                                    {glider?.twoSeat && (
                                        <IonItem onClick={() => navigateTo(DetailType.GLIDER_SECOND)}>
                                            <IonLabel>Druhý pilot</IonLabel>
                                            <div>
                                                {second ? completePilotName(second) : "žádný"}
                                            </div>
                                        </IonItem>
                                    )}

                                    <IonItem>
                                        <IonLabel>Přistání kluzáku</IonLabel>
                                        <TimeInput
                                            value={gliderLanding}
                                            id={id}
                                            min={start}
                                            type={TimeType.LANDING_GLIDER_TIME}
                                            timeChange={handleTimeChange}
                                        />
                                    </IonItem>
                                </>
                            )
                        )}

                        {/* Additional fields for non-suggestions */}
                        {!isSuggestion && (
                            <>
                                <IonItem onClick={() => navigateTo(DetailType.NOTE)}>
                                    <IonLabel>Poznámka</IonLabel>
                                    <span>{note}</span>
                                </IonItem>

                                <IonItem onClick={() => navigateTo(DetailType.TAKEOFF_PLACE)}>
                                    <IonLabel>Místo vzletu</IonLabel>
                                    <span>{takeOffPlace?.name || ""}</span>
                                </IonItem>

                                <IonItem onClick={() => navigateTo(DetailType.LANDING_PLACE)}>
                                    <IonLabel>Místo přistání</IonLabel>
                                    <span>{landingPlace?.name || ""}</span>
                                </IonItem>
                            </>
                        )}
                    </IonList>
                </IonContent>

                <IonFooter>
                    <IonRow>
                        <IonCol sizeXs="12" sizeMd="4" offsetMd="4">
                            {isSuggestion ? (
                                <IonButton
                                    expand="full"
                                    onClick={handleCreateAndHome}
                                    color="primary"
                                    disabled={!isFormValid}
                                >
                                    <IonIcon icon={save} />
                                    <span>Uložit</span>
                                </IonButton>
                            ) : (
                                <IonButton
                                    expand="full"
                                    onClick={home}
                                    color="primary"
                                >
                                    <IonIcon icon={arrowBack} />
                                    <span>Zpět</span>
                                </IonButton>
                            )}
                        </IonCol>
                    </IonRow>
                </IonFooter>

                {renderDeleteModal()}
            </IonPage>
        );
    };

    // Main render logic
    const renderCurrentView = () => {
        switch (view) {
            case DetailType.GLIDER_FIRST:
            case DetailType.GLIDER_SECOND:
            case DetailType.TOWING_PILOT:
                return renderPilotView();
            case DetailType.NOTE:
                return renderNoteView();
            case DetailType.GLIDER:
                return renderGliderView();
            case DetailType.TOWING_PLANE:
                return renderTowingPlaneView();
            case DetailType.TAKEOFF_PLACE:
            case DetailType.LANDING_PLACE:
                return renderPlaceView();
            case DetailType.TOWING_TASK:
                return renderTowingTaskView();
            default:
                return renderHomeView();
        }
    };

    return renderCurrentView();
};

export default AeroTowDetail;